import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AxiosInstance from "../Home/AxiosInstance";
import { storeTokens } from "../Home/auth";
import "./Company.css";
import "./EmployerSignIn.css";
import "../Home/publicPages.css";
import "../Home/authPages.css";
import "bootstrap/dist/css/bootstrap.min.css";

const BASE_PATH = process.env.REACT_APP_BASE_PATH || "/als";

export default function EmployerSignIn() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup | forgot

  const [signUpData, setSignUpData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobileNo: "",
    password: "",
    confirmPassword: "",
  });
  const [signInData, setSignInData] = useState({
    email: "",
    password: "",
    role: "Company_Admin",
  });
  const [signUpError, setSignUpError] = useState("");
  const [signUpSuccess, setSignUpSuccess] = useState("");
  const [signInError, setSignInError] = useState("");

  const [forgotEmail, setForgotEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [forgotError, setForgotError] = useState("");

  const goToSignIn = () => {
    setMode("signin");
    setSignInError("");
    setSignUpError("");
    setSignUpSuccess("");
    setForgotError("");
  };

  const goToSignUp = () => {
    setMode("signup");
    setSignInError("");
    setSignUpError("");
    setSignUpSuccess("");
  };

  const goToForgot = () => {
    setMode("forgot");
    setForgotEmail("");
    setOtp("");
    setNewPassword("");
    setIsOtpSent(false);
    setForgotError("");
    setSignInError("");
  };

  const handleSignUpChange = (e) => {
    setSignUpData({ ...signUpData, [e.target.id]: e.target.value });
    setSignUpError("");
    setSignUpSuccess("");
  };

  const handleSignInChange = (e) => {
    setSignInData({ ...signInData, [e.target.name]: e.target.value });
    setSignInError("");
  };

  const handleSignUpSubmit = async (e) => {
    e.preventDefault();
    const { firstName, lastName, email, mobileNo, password, confirmPassword } =
      signUpData;

    if (!firstName || !email || !mobileNo || !password || !confirmPassword) {
      setSignUpError("Please fill in all required fields.");
      return;
    }

    if (password.length < 6) {
      setSignUpError("Password must be at least 6 characters long.");
      return;
    }

    if (password !== confirmPassword) {
      setSignUpError("Passwords do not match.");
      return;
    }

    const payload = {
      first_name: firstName,
      last_name: lastName,
      email,
      mobile_no: mobileNo,
      password,
      role: "Company_Admin",
    };

    try {
      await AxiosInstance.post("companies/user/signup", payload);
      setSignUpSuccess("Account created successfully! You can now sign in.");
      setSignUpError("");
      setMode("signin");
    } catch (error) {
      let errorMessage = "Failed to sign up. Please try again.";

      if (error.response && error.response.data) {
        const errorData = error.response.data.error;

        if (typeof errorData === "string") {
          errorMessage = errorData;
        } else if (typeof errorData === "object" && errorData !== null) {
          const firstKey = Object.keys(errorData)[0];
          const firstError = errorData[firstKey];
          if (Array.isArray(firstError)) {
            errorMessage = firstError[0];
          }
        }

        if (
          errorMessage.toLowerCase().includes("email") &&
          errorMessage.toLowerCase().includes("already exists")
        ) {
          errorMessage = "User already exists with this email";
        }
      }

      setSignUpError(errorMessage);
      setSignUpSuccess("");
    }
  };

  const handleSignInSubmit = async (e) => {
    e.preventDefault();
    const { email, password, role } = signInData;

    if (!email || !password || !role) {
      setSignInError("Please enter email, password, and select a role!");
      return;
    }

    try {
      const response = await AxiosInstance.post(
        `companies/user/signin/${email}/`,
        { password, role }
      );

      const data = response.data;
      const userRole = Array.isArray(data.roles) ? data.roles[0] : data.role;

      storeTokens(data);
      localStorage.setItem("userId", data.user_id);
      localStorage.setItem("role", userRole);
      localStorage.setItem("email", email);

      if (userRole === "Company_Admin") {
        navigate("/company-details");
      } else {
        navigate("/user-profile");
      }
    } catch (error) {
      if (error.response) {
        setSignInError(error.response.data.error || "Invalid credentials.");
      } else {
        setSignInError("An error occurred. Please try again.");
      }
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();

    if (!forgotEmail) {
      setForgotError("Please enter your email.");
      return;
    }

    try {
      const response = await AxiosInstance.post(
        "companies/forgot-password/otp/",
        {
          email: forgotEmail,
          user_type: "user",
        }
      );

      setIsOtpSent(true);
      setForgotError("");
      alert(response.data.message || "OTP sent to your email!");
    } catch (error) {
      let errorMessage = "Server error. Please try again.";
      if (error.response && error.response.data) {
        errorMessage = error.response.data.error || errorMessage;
      }
      setForgotError(errorMessage);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!otp || !newPassword) {
      setForgotError("Please enter OTP and new password.");
      return;
    }

    try {
      const response = await AxiosInstance.post(
        "companies/forgot-password/reset/",
        {
          email: forgotEmail,
          otp,
          new_password: newPassword,
          user_type: "user",
        }
      );

      alert(response.data.message || "Password reset successfully!");
      goToSignIn();
    } catch (error) {
      let errorMessage = "Failed to reset password.";
      if (error.response && error.response.data) {
        errorMessage = error.response.data.error || errorMessage;
      }
      setForgotError(errorMessage);
    }
  };

  return (
    <div className="EmployerPage als-auth-page employer-auth-page">
      <div className="als-auth-shell">
        <aside className="als-auth-brand">
          <div className="als-auth-brand-inner">
            <p className="als-auth-eyebrow">Employer portal</p>
            {mode === "forgot" ? (
              <>
                <h1>Reset Your Password</h1>
                <p>
                  We&apos;ll send an OTP to your email so you can securely set a
                  new password and return to your employer account.
                </p>
                <ul className="als-auth-points">
                  <li>
                    <span>✓</span> Verify with a one-time OTP
                  </li>
                  <li>
                    <span>✓</span> Create a new secure password
                  </li>
                  <li>
                    <span>✓</span> Sign in and continue hiring
                  </li>
                </ul>
              </>
            ) : (
              <>
                <h1>Hire talent ready for day one.</h1>
                <p>
                  Sign in to post internships, review applicants, and grow your next
                  generation team with Open Learnings.
                </p>
                <ul className="als-auth-points">
                  <li>
                    <span>✓</span> Post and manage internship openings
                  </li>
                  <li>
                    <span>✓</span> Reach job-ready learners
                  </li>
                  <li>
                    <span>✓</span> Build your company hiring presence
                  </li>
                </ul>
              </>
            )}
          </div>
        </aside>
        <div className="als-auth-panel">
          <div className="auth-forms-container">
            {mode === "signup" ? (
              <div className="signup-container">
                <h3>Create employer account</h3>
                <p className="als-auth-lead">
                  Register your company admin account to start hiring.
                </p>
                <form onSubmit={handleSignUpSubmit}>
                  <div className="form-row d-flex gap-3">
                    <div className="flex-fill">
                      <label htmlFor="firstName">First Name</label>
                      <input
                        type="text"
                        id="firstName"
                        className="form-control"
                        required
                        value={signUpData.firstName}
                        onChange={handleSignUpChange}
                      />
                    </div>
                    <div className="flex-fill">
                      <label htmlFor="lastName">Last Name</label>
                      <input
                        type="text"
                        id="lastName"
                        className="form-control"
                        required
                        value={signUpData.lastName}
                        onChange={handleSignUpChange}
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      className="form-control"
                      required
                      value={signUpData.email}
                      onChange={handleSignUpChange}
                    />
                  </div>

                  <div className="mt-3">
                    <label htmlFor="mobileNo">Mobile No</label>
                    <input
                      type="text"
                      id="mobileNo"
                      className="form-control"
                      required
                      value={signUpData.mobileNo}
                      onChange={handleSignUpChange}
                    />
                  </div>

                  <div className="mt-3">
                    <label htmlFor="password">Password</label>
                    <input
                      type="password"
                      id="password"
                      className="form-control"
                      required
                      value={signUpData.password}
                      onChange={handleSignUpChange}
                    />
                  </div>

                  <div className="mt-3">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      className="form-control"
                      required
                      value={signUpData.confirmPassword}
                      onChange={handleSignUpChange}
                    />
                  </div>

                  {signUpError && (
                    <div className="alert alert-danger mt-2">{signUpError}</div>
                  )}
                  {signUpSuccess && (
                    <div className="alert alert-success mt-2">
                      {signUpSuccess}
                    </div>
                  )}

                  <button type="submit" className="btn1 mt-4 w-100">
                    Sign Up
                  </button>
                </form>

                <div className="d-flex justify-content-between mt-4 flex-wrap">
                  <small className="signup-text">
                    Already have an account?{" "}
                    <span
                      onClick={goToSignIn}
                      className="green-hover-link1"
                      style={{ cursor: "pointer" }}
                    >
                      Sign In
                    </span>
                  </small>
                  <small className="signup-text">
                    <Link to="/" className="green-hover-link1">
                      Home
                    </Link>
                  </small>
                </div>
              </div>
            ) : mode === "forgot" ? (
              <div className="forgot-password-container">
                <h3>{isOtpSent ? "Reset Password" : "Forgot Password"}</h3>
                <p className="als-auth-lead">
                  {isOtpSent
                    ? "Enter the OTP sent to your email and set a new password."
                    : "We'll send an OTP to your email so you can set a new password."}
                </p>
                {!isOtpSent ? (
                  <form onSubmit={handleRequestOtp}>
                    <div className="mb-3">
                      <label htmlFor="forgotEmail" className="form-label">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="forgotEmail"
                        className="form-control"
                        placeholder="Enter your email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                      />
                    </div>
                    {forgotError && (
                      <div className="alert alert-danger">{forgotError}</div>
                    )}
                    <button type="submit" className="btn1 w-100">
                      Request OTP
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleResetPassword}>
                    <div className="mb-3">
                      <label htmlFor="otp" className="form-label">
                        OTP
                      </label>
                      <input
                        type="text"
                        id="otp"
                        className="form-control"
                        placeholder="Enter the OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="newPassword" className="form-label">
                        New Password
                      </label>
                      <input
                        type="password"
                        id="newPassword"
                        className="form-control"
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                      />
                    </div>
                    {forgotError && (
                      <div className="alert alert-danger">{forgotError}</div>
                    )}
                    <button type="submit" className="btn1 w-100">
                      Reset Password
                    </button>
                  </form>
                )}

                <div className="als-auth-footer-links">
                  <small>
                    Remember your password?{" "}
                    <span
                      onClick={goToSignIn}
                      className="green-hover-link1"
                      style={{ cursor: "pointer" }}
                    >
                      Sign In
                    </span>
                  </small>
                  <small>
                    <Link to="/" className="green-hover-link1">
                      Home
                    </Link>
                  </small>
                </div>
              </div>
            ) : (
              <div className="signin-container">
                <h3>Welcome back</h3>
                <p className="als-auth-lead">
                  Sign in as an employer admin or employee to continue.
                </p>
                <form onSubmit={handleSignInSubmit}>
                  <div className="als-auth-role-toggle">
                    <label
                      className={
                        signInData.role === "Company_Admin" ? "active" : ""
                      }
                    >
                      <input
                        type="radio"
                        name="role"
                        value="Company_Admin"
                        checked={signInData.role === "Company_Admin"}
                        onChange={handleSignInChange}
                      />
                      Admin
                    </label>
                    <label
                      className={signInData.role === "HR" ? "active" : ""}
                    >
                      <input
                        type="radio"
                        name="role"
                        value="HR"
                        checked={signInData.role === "HR"}
                        onChange={handleSignInChange}
                      />
                      Employee
                    </label>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="signin-email" className="form-label">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="signin-email"
                      name="email"
                      className="form-control"
                      placeholder="Enter your email"
                      value={signInData.email}
                      onChange={handleSignInChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="signin-password" className="form-label">
                      Password
                    </label>
                    <input
                      type="password"
                      id="signin-password"
                      name="password"
                      className="form-control"
                      placeholder="Enter your password"
                      value={signInData.password}
                      onChange={handleSignInChange}
                      required
                    />
                  </div>

                  {signInError && (
                    <div className="alert alert-danger" role="alert">
                      {signInError}
                    </div>
                  )}
                  {signUpSuccess && (
                    <div className="alert alert-success">{signUpSuccess}</div>
                  )}

                  <div className="d-flex justify-content-end mb-3">
                    <span
                      className="green-hover-link1"
                      style={{ cursor: "pointer" }}
                      onClick={goToForgot}
                    >
                      Forgot Password?
                    </span>
                  </div>

                  <div className="d-grid mt-3">
                    <button type="submit" className="btn1">
                      Sign In
                    </button>
                  </div>
                </form>

                <div className="mt-3 d-flex justify-content-between">
                  <small className="signup-text">
                    Don’t have an account?{" "}
                    <span
                      className="green-hover-link1"
                      style={{ cursor: "pointer" }}
                      onClick={goToSignUp}
                    >
                      Sign Up
                    </span>
                  </small>
                  <small className="signin-text">
                    <Link to="/" className="green-hover-link1">
                      Home
                    </Link>
                  </small>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
